import React from 'react';
import type { TabParamList } from '../../navigation/types';
import type { MenuTabBarIconFlow } from '../../shared/navigation/menuTabResolver';
import { SvgIcon, type SvgIconName } from '../../components/common/SvgIcon';

export type TabBarSvgIconPair = { default: SvgIconName; focused: SvgIconName };

export type TabBarSvgIconOptions = {
  menu_tab_flow?: MenuTabBarIconFlow;
};

/** v3 Android tab glyphs — mirrors `TAB_BAR_SF_SYMBOLS.v3` on iOS. */
export const TAB_BAR_SVG_ICONS: Record<keyof TabParamList, TabBarSvgIconPair> = {
  Calendar: { default: 'calendar', focused: 'calendar' },
  DailyMenuFlow: { default: 'meal', focused: 'meal' },
  Dashboard: { default: 'dashboard-outline', focused: 'dashboard' },
  Order: { default: 'package', focused: 'package' },
  Account: { default: 'users', focused: 'users' },
};

export function getTabBarSvgIconNames(
  route_name: keyof TabParamList,
  focused?: boolean,
): SvgIconName {
  const pair = TAB_BAR_SVG_ICONS[route_name];
  return focused ? pair.focused : pair.default;
}

type TabBarIconProps = { focused: boolean; color: string; size: number };

function TabBarIconInner({
  route_name,
  focused,
  color,
  size,
}: TabBarIconProps & { route_name: keyof TabParamList }) {
  const icon_name = getTabBarSvgIconNames(route_name, focused);
  const icon_size = focused ? size + 2 : size;
  return <SvgIcon name={icon_name} size={icon_size} color={color} />;
}

export function createTabBarIcon(route_name: keyof TabParamList) {
  return function TabBarIcon(props: TabBarIconProps) {
    return <TabBarIconInner route_name={route_name} {...props} />;
  };
}

const tab_bar_icon_cache: Partial<Record<keyof TabParamList, ReturnType<typeof createTabBarIcon>>> = {};

export function getTabBarIcon(route_name: keyof TabParamList, _opts?: TabBarSvgIconOptions) {
  if (!tab_bar_icon_cache[route_name]) {
    tab_bar_icon_cache[route_name] = createTabBarIcon(route_name);
  }
  return tab_bar_icon_cache[route_name]!;
}
